<template>
  <v-dialog
    :model-value="modelValue && !isCancelling"
    :max-width="['calling', 'not-answered', 'breakthrough', 'completed'].includes(callState) ? '510' : '568'"
    persistent
    @update:model-value="handleClose"
  >
    <!-- Hide all content while cancelling to prevent flash -->
    <v-card v-if="isCancelling" class="call-dialog" style="height: 0; overflow: hidden;"></v-card>
    
    <v-card
      v-else
      class="call-dialog"
      :class="{
        'call-dialog--calling': callState === 'calling',
        'call-dialog--not-answered': callState === 'not-answered',
        'call-dialog--breakthrough': callState === 'breakthrough',
        'call-dialog--completed': callState === 'completed',
      }"
    >
      <v-btn icon variant="text" size="default" class="call-dialog__close-btn" @click="handleClose">
        <v-icon size="24" color="#222222">mdi-close</v-icon>
      </v-btn>

      <div v-if="callState === 'calling'" class="call-dialog__calling-state">
        <div class="call-dialog__calling-content">
          <div class="call-dialog__calling-avatar">
            <div class="call-dialog__calling-avatar-inner">
              <div class="call-dialog__calling-avatar-pulse"></div>
              <div class="call-dialog__calling-initials">{{ patientInitials }}</div>
            </div>
          </div>
          <div class="call-dialog__calling-info">
            <h3 class="call-dialog__calling-title">Calling {{ patientInfo?.patientName?.split(" ")[0] }}...</h3>
            <div class="call-dialog__calling-status">Waiting for them to accept</div>
            <div class="call-dialog__calling-timer">{{ formattedDuration }}</div>
          </div>
        </div>
      </div>

      <v-btn
        v-if="callState === 'calling'"
        size="large"
        rounded="pill"
        class="call-dialog__cancel-btn"
        @click="handleCancelCall"
      >
        Cancel Call
        <v-icon end size="24" color="#FFFFFF">mdi-close</v-icon>
      </v-btn>

      <div v-else-if="callState === 'breakthrough'" ref="breakthroughStateRef" class="call-dialog__breakthrough-state">
        <v-btn icon variant="text" size="default" class="call-dialog__back-btn" @click="handleBackFromBreakthrough">
          <v-icon size="24" color="#222222">mdi-arrow-left</v-icon>
        </v-btn>

        <div class="call-dialog__breakthrough-content">
          <div class="call-dialog__breakthrough-icon">
            <v-icon size="82" color="#D79D17">mdi-alert-circle</v-icon>
          </div>
          <div class="call-dialog__breakthrough-warning">
            Breakthrough immediately connects to patient device.<br />
            Overrides quiet hours and patient settings.
          </div>
          <div class="call-dialog__breakthrough-reasons">
            <div class="call-dialog__breakthrough-label">Select reason for override</div>
            <div class="call-dialog__breakthrough-options">
              <div v-for="reason in breakthroughReasons" :key="reason.id">
                <div
                  :class="[
                    'call-dialog__breakthrough-option',
                    { 
                      'call-dialog__breakthrough-option--selected': selectedBreakthroughReason === reason.id,
                      'call-dialog__breakthrough-option--disabled': isBreakthroughInProgress
                    },
                  ]"
                  role="button"
                  tabindex="0"
                  @click="!isBreakthroughInProgress && handleReasonSelect(reason.id)"
                  @keydown.enter="!isBreakthroughInProgress && handleReasonSelect(reason.id)"
                  @keydown.space.prevent="!isBreakthroughInProgress && handleReasonSelect(reason.id)"
                >
                  <span class="call-dialog__breakthrough-option-text">{{ reason.label }}</span>
                  <v-icon v-if="!isBreakthroughInProgress" size="24" color="#291758">
                    {{
                      reason.id === "other"
                        ? showOtherInput
                          ? "mdi-chevron-up"
                          : "mdi-chevron-down"
                        : "mdi-chevron-right"
                    }}
                  </v-icon>
                  <v-progress-circular
                    v-else-if="selectedBreakthroughReason === reason.id && reason.id !== 'other'"
                    indeterminate
                    size="20"
                    width="2"
                    color="primary"
                  />
                </div>
                <v-expand-transition>
                  <div v-if="reason.id === 'other' && showOtherInput" class="call-dialog__other-input-wrapper">
                    <v-text-field
                      v-model="otherReasonText"
                      variant="outlined"
                      density="comfortable"
                      placeholder="Enter custom reason..."
                      hide-details
                      class="call-dialog__other-input"
                      :disabled="isBreakthroughInProgress"
                    />
                    <v-expand-transition>
                      <v-btn
                        v-if="otherReasonText.trim()"
                        size="large"
                        rounded="pill"
                        class="call-dialog__continue-btn"
                        :loading="isBreakthroughInProgress"
                        :disabled="isBreakthroughInProgress"
                        @click="handleOtherReasonSubmit"
                      >
                        Continue
                      </v-btn>
                    </v-expand-transition>
                  </div>
                </v-expand-transition>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="callState === 'completed'" class="call-dialog__completed-state">
        <div class="call-dialog__completed-header">
          <h3 class="call-dialog__completed-title">Call log: Room {{ patientInfo?.room.roomNumber }}</h3>
          <div class="call-dialog__completed-patient">{{ patientInfo?.patientName }}</div>
        </div>

        <div class="call-dialog__completed-status">
          <div
            class="call-dialog__status-badge"
            :style="{ backgroundColor: statusBadgeColor, color: statusBadgeTextColor }"
          >
            {{ displayReason }}
          </div>
          <div v-if="patientInfo?.dischargeOrderTime" class="call-dialog__discharge-badge">
            Discharge order: {{ formatDischargeTime }}
          </div>
        </div>

        <div class="call-dialog__completed-sections">
          <div class="call-dialog__section">
            <div
              class="call-dialog__section-header"
              role="button"
              tabindex="0"
              @click="toggleCallParticipants"
              @keydown.enter="toggleCallParticipants"
              @keydown.space.prevent="toggleCallParticipants"
            >
              <div class="call-dialog__section-header-left">
                <span class="call-dialog__section-title">CALL PARTICIPANTS</span>
                <div class="call-dialog__badge">2</div>
              </div>
              <v-icon size="16" color="#6A5D8A">
                {{ callParticipantsExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
              </v-icon>
            </div>
            <v-expand-transition>
              <div v-show="callParticipantsExpanded" class="call-dialog__completed-participants">
                <div class="call-dialog__completed-participant">
                  <div class="call-dialog__avatar">{{ patientInitials }}</div>
                  <div class="call-dialog__participant-info">
                    <div class="call-dialog__participant-name">{{ patientInfo?.patientName }}</div>
                    <div class="call-dialog__participant-role">Patient</div>
                  </div>
                </div>
                <div class="call-dialog__completed-participant">
                  <div class="call-dialog__avatar call-dialog__avatar--nurse">AM</div>
                  <div class="call-dialog__participant-info">
                    <div class="call-dialog__participant-name">Alex Mendez</div>
                    <div class="call-dialog__participant-role">RN (You)</div>
                  </div>
                </div>
              </div>
            </v-expand-transition>
          </div>

          <div class="call-dialog__section">
            <div
              class="call-dialog__section-header"
              role="button"
              tabindex="0"
              @click="toggleCallDetails"
              @keydown.enter="toggleCallDetails"
              @keydown.space.prevent="toggleCallDetails"
            >
              <span class="call-dialog__section-title">CALL DETAILS</span>
              <v-icon size="16" color="#6A5D8A">
                {{ callDetailsExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
              </v-icon>
            </div>
            <v-expand-transition>
              <div v-show="callDetailsExpanded" class="call-dialog__completed-details">
                <div class="call-dialog__detail-row">
                  <div class="call-dialog__detail-label">Attempt Count</div>
                  <div class="call-dialog__detail-value">{{ callAttempts }}</div>
                </div>
                <div class="call-dialog__detail-row">
                  <div class="call-dialog__detail-label">Timestamp</div>
                  <div class="call-dialog__detail-value">Today, {{ callStartTime }}</div>
                </div>
                <div class="call-dialog__detail-row">
                  <div class="call-dialog__detail-label">Call duration</div>
                  <div class="call-dialog__detail-value">{{ formattedCallDurationFull }}</div>
                </div>
                <div class="call-dialog__detail-row">
                  <div class="call-dialog__detail-label">Outcome</div>
                  <div class="call-dialog__detail-value">{{ callOutcome }}</div>
                </div>
              </div>
            </v-expand-transition>
          </div>

          <div v-show="feedbackExpanded" class="call-dialog__section">
            <div class="call-dialog__section-header">
              <span class="call-dialog__section-title">FEEDBACK</span>
            </div>
            <div class="call-dialog__feedback-section">
              <v-textarea
                v-model="feedbackText"
                variant="outlined"
                placeholder="Share your feedback about this call..."
                hide-details
                rows="3"
                no-resize
                class="call-dialog__feedback-textarea"
              />
            </div>
          </div>
        </div>

        <div class="call-dialog__completed-footer">
          <div v-if="!feedbackExpanded" class="call-dialog__footer-actions">
            <v-btn
              variant="outlined"
              rounded="pill"
              size="default"
              class="call-dialog__feedback-btn"
              @click="handleLeaveFeedback"
            >
              Leave Feedback
            </v-btn>
          </div>
          <div v-else class="call-dialog__footer-actions call-dialog__footer-actions--feedback">
            <v-btn
              variant="outlined"
              rounded="pill"
              size="default"
              class="call-dialog__close-btn-footer"
              @click="handleClose"
            >
              Close
            </v-btn>
            <v-btn
              rounded="pill"
              size="default"
              class="call-dialog__submit-btn"
              :disabled="!feedbackText.trim()"
              @click="handleSubmitFeedback"
            >
              Submit
            </v-btn>
          </div>
        </div>
      </div>

      <div v-else-if="callState === 'not-answered'" class="call-dialog__not-answered-state">
        <div class="call-dialog__not-answered-content">
          <div class="call-dialog__not-answered-icon">
            <v-icon size="82" color="#7F8287">mdi-minus-circle-outline</v-icon>
          </div>
          <div class="call-dialog__not-answered-info">
            <h3 class="call-dialog__not-answered-title">No Answer</h3>
            <div class="call-dialog__not-answered-subtitle">
              Call attempt #{{ callAttempts }} logged<br />
              Duration: {{ formattedCallDuration }} • {{ callEndTime }}
            </div>
          </div>
        </div>
        <div class="call-dialog__not-answered-actions">
          <v-btn size="large" rounded="pill" class="call-dialog__call-again-btn" @click="handleCallAgain">
            <v-icon start size="24" color="#FFFFFF">mdi-bell-ring-outline</v-icon>
            Call Again
            <v-icon end size="20" color="#FFFFFF">mdi-arrow-right</v-icon>
          </v-btn>
          <div class="call-dialog__breakthrough-section">
            <v-btn size="large" rounded="pill" class="call-dialog__breakthrough-btn" @click="handleBreakthroughCall">
              <v-icon start size="24" color="#222222">mdi-lock-open-remove</v-icon>
              Breakthrough
              <v-icon end size="20" color="#222222">mdi-arrow-right</v-icon>
            </v-btn>
            <div class="call-dialog__breakthrough-text">
              Breakthrough immediately connects to patient device.<br />
              Overrides quiet hours and patient settings.
            </div>
          </div>
        </div>
      </div>

      <div v-else class="call-dialog__content">
        <div class="call-dialog__header">
          <h2 class="call-dialog__title">Call patient in Room {{ patientInfo?.room.roomNumber }}</h2>
          <div class="call-dialog__patient-name">
            {{ patientInfo?.patientName }}
          </div>

          <div class="call-dialog__status-column">
            <div
              class="call-dialog__status-badge"
              :style="{ backgroundColor: statusBadgeColor, color: statusBadgeTextColor }"
            >
              {{ displayReason }}
            </div>
            <div v-if="patientInfo?.dischargeOrderTime" class="call-dialog__discharge-badge">
              Discharge order: {{ formatDischargeTime }}
            </div>
            <div v-if="patientInfo?.isOverdue" class="call-dialog__overdue-badge">
              <v-icon size="16" color="#FFFFFF">mdi-alert-circle</v-icon>
              <span>{{ overdueMinutes }} overdue</span>
            </div>
          </div>
        </div>

        <div class="call-dialog__scrollable-content">
          <v-alert type="info" variant="tonal" class="call-dialog__alert" density="comfortable">
            <template #prepend>
              <v-icon>mdi-information</v-icon>
            </template>
            <div class="call-dialog__alert-text">Spanish Interpreter Required</div>
          </v-alert>

          <div class="call-dialog__section">
            <div
              class="call-dialog__section-header"
              role="button"
              tabindex="0"
              @click="toggleCallParticipants"
              @keydown.enter="toggleCallParticipants"
              @keydown.space.prevent="toggleCallParticipants"
            >
              <div class="call-dialog__section-header-left">
                <span class="call-dialog__section-title">CALL PARTICIPANTS</span>
                <div class="call-dialog__badge">2</div>
              </div>
              <v-icon size="20" color="#6A5D8A">
                {{ callParticipantsExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
              </v-icon>
            </div>
            <v-expand-transition>
              <div v-show="callParticipantsExpanded">
                <div class="call-dialog__participants">
                  <div class="call-dialog__participant">
                    <div class="call-dialog__avatar-wrapper">
                      <div class="call-dialog__avatar">{{ patientInitials }}</div>
                      <div class="call-dialog__status-dot"></div>
                    </div>
                    <div class="call-dialog__participant-info">
                      <div class="call-dialog__participant-name">{{ patientInfo?.patientName }}</div>
                      <div class="call-dialog__participant-role">Patient</div>
                    </div>
                    <v-btn icon variant="text" size="small" disabled>
                      <v-icon size="20">mdi-microphone</v-icon>
                    </v-btn>
                  </div>
                  <div class="call-dialog__participant">
                    <div class="call-dialog__avatar-wrapper">
                      <div class="call-dialog__avatar call-dialog__avatar--nurse">{{ currentUserInitials }}</div>
                      <div class="call-dialog__status-dot"></div>
                    </div>
                    <div class="call-dialog__participant-info">
                      <div class="call-dialog__participant-name">{{ currentUserDisplayName }}</div>
                      <div class="call-dialog__participant-role">RN (You)</div>
                    </div>
                    <v-btn icon variant="text" size="small" disabled>
                      <v-icon size="20">mdi-microphone</v-icon>
                    </v-btn>
                  </div>
                </div>
                <div class="call-dialog__action-buttons">
                  <v-btn variant="flat" size="small" rounded="pill" class="call-dialog__add-btn">
                    <v-icon start size="20">mdi-account-multiple</v-icon>
                    Add Staff
                  </v-btn>
                  <v-btn variant="flat" size="small" rounded="pill" class="call-dialog__add-btn">
                    <v-icon start size="20">mdi-translate</v-icon>
                    Add Interpreter
                  </v-btn>
                </div>
              </div>
            </v-expand-transition>
          </div>

          <div class="call-dialog__section">
            <div
              class="call-dialog__section-header"
              role="button"
              tabindex="0"
              @click="toggleCarePartners"
              @keydown.enter="toggleCarePartners"
              @keydown.space.prevent="toggleCarePartners"
            >
              <div class="call-dialog__section-header-left">
                <span class="call-dialog__section-title">CARE PARTNERS</span>
                <div class="call-dialog__badge">2</div>
              </div>
              <v-icon size="20" color="#6A5D8A">
                {{ carePartnersExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
              </v-icon>
            </div>
            <v-expand-transition>
              <div v-show="carePartnersExpanded">
                <div class="call-dialog__participants">
                  <div class="call-dialog__participant">
                    <div class="call-dialog__avatar">AJ</div>
                    <div class="call-dialog__participant-info">
                      <div class="call-dialog__participant-name">Andrew Johnson</div>
                      <div class="call-dialog__participant-role">Husband</div>
                    </div>
                    <v-btn icon variant="text" size="small" disabled>
                      <v-icon size="20">mdi-phone</v-icon>
                    </v-btn>
                  </div>
                  <div class="call-dialog__participant">
                    <div class="call-dialog__avatar">GF</div>
                    <div class="call-dialog__participant-info">
                      <div class="call-dialog__participant-name">Gabrielle Franco</div>
                      <div class="call-dialog__participant-role">Daughter</div>
                    </div>
                    <v-btn icon variant="text" size="small" disabled>
                      <v-icon size="20">mdi-phone</v-icon>
                    </v-btn>
                  </div>
                </div>
                <div class="call-dialog__action-buttons">
                  <v-btn variant="flat" size="small" rounded="pill" class="call-dialog__add-btn">
                    <v-icon start size="20">mdi-account-multiple</v-icon>
                    Add Care Partner
                  </v-btn>
                </div>
              </div>
            </v-expand-transition>
          </div>
        </div>
      </div>

      <div class="call-dialog__footer">
        <v-btn size="large" rounded="pill" class="call-dialog__call-btn" @click="handleConfirm">
          <v-icon start color="#FFFFFF" size="20">mdi-bell-ring-outline</v-icon>
          Call Now
          <v-icon end color="#FFFFFF" size="20">mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </v-card>

    <!-- Error Snackbar -->
    <v-snackbar
      v-model="showError"
      color="error"
      timeout="6000"
      location="top"
    >
      <v-icon start>mdi-alert-circle</v-icon>
      {{ errorMessage }}
      <template #actions>
        <v-btn
          variant="text"
          @click="showError = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-dialog>
</template>

<script lang="ts" setup>
import { computed, ref, watch, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import type { Patient } from "@virtual-care/shared";
import { signalingService } from "../services/signalingService";
import { callApiClient } from "../services/callApiClient";
import { useCallStore } from "../store/callStore";
import { usePatientStore } from "../store/patientStore";
import { formatElapsedTime, formatOverdueTime } from "../utils/timeFormat";

interface Props {
  modelValue: boolean;
  patientInfo?: Patient | null;
  showBreakthrough?: boolean;
}

interface Emits {
  (e: "update:modelValue", value: boolean): void;
  (e: "confirm"): void;
}

const props = withDefaults(defineProps<Props>(), {
  patientInfo: null,
  showBreakthrough: false,
});
const emit = defineEmits<Emits>();

const router = useRouter();
const callStore = useCallStore();
const patientStore = usePatientStore();

// Derive patientId from roomId stored in callStore (roomId alone is not PHI).
// Must be called BEFORE callStore.endCall() which nulls out roomId.
const refreshCallLogFromRoomId = () => {
  const roomId = callStore.roomId;
  if (!roomId) return;
  const patient = patientStore.patients.find((p) => p.room.roomId === roomId);
  if (patient) {
    patientStore.refreshPatientCallLog(patient.patientId).catch(() => {});
  }
};

const breakthroughStateRef = ref<HTMLElement | null>(null);
const callParticipantsExpanded = ref(true);
const carePartnersExpanded = ref(true);
const callDetailsExpanded = ref(true);
const feedbackExpanded = ref(false);
const callState = ref<"ready" | "calling" | "not-answered" | "breakthrough" | "completed">("ready");
const previousState = ref<"ready" | "not-answered">("ready");
const callDuration = ref(0);
const callAttempts = ref(1);
const callEndTime = ref("");
const callStartTime = ref("");
const callOutcome = ref("");
const selectedBreakthroughReason = ref<string | null>(null);
const showOtherInput = ref(false);
const otherReasonText = ref("");
const feedbackText = ref("");
const isBreakthroughInProgress = ref(false);
const isCancelling = ref(false); // Prevent state changes during cancellation
const showError = ref(false);
const errorMessage = ref("");
let timerInterval: number | null = null;
let noAnswerTimeout: number | null = null;

// Get current user info from localStorage
const currentUserDisplayName = computed(() => {
  return localStorage.getItem('vc_display_name') || 'Unknown User';
});

const currentUserInitials = computed(() => {
  const name = currentUserDisplayName.value;
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
});

watch(otherReasonText, () => {
  if (otherReasonText.value && breakthroughStateRef.value) {
    setTimeout(() => {
      breakthroughStateRef.value?.scrollTo({
        top: breakthroughStateRef.value.scrollHeight,
        behavior: "smooth",
      });
    }, 100);
  }
});

const breakthroughReasons = [
  { id: "sla-breach", label: "SLA Breach – Discharge Overdue" },
  { id: "clinical-need", label: "Time-Sensitive Clinical Need" },
  { id: "transportation", label: "Transportation / DME Arrived" },
  { id: "other", label: "Other" },
];

const toggleCallParticipants = () => {
  callParticipantsExpanded.value = !callParticipantsExpanded.value;
};

const toggleCarePartners = () => {
  carePartnersExpanded.value = !carePartnersExpanded.value;
};

const startTimer = () => {
  callDuration.value = 0;
  timerInterval = window.setInterval(() => {
    callDuration.value++;
  }, 1000);
};

const stopTimer = () => {
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
  if (noAnswerTimeout) {
    clearTimeout(noAnswerTimeout);
    noAnswerTimeout = null;
  }
};

const removeSocketListeners = () => {
  signalingService.off("call_accepted", onCallAccepted);
  signalingService.off("call_declined", onCallDeclined);
  signalingService.off("call_ended", onCallEnded);
};

const onCallAccepted = () => {
  removeSocketListeners();
  stopTimer();
  const sessionId = callStore.sessionId;
  if (sessionId) {
    router.push(`/nurse-console/call/${sessionId}`);
    emit('update:modelValue', false); // Close the dialog
  }
};

const onCallDeclined = () => {
  // Ignore if we're actively cancelling (prevents flash)
  if (isCancelling.value) {
    console.log('[CallDialog] Ignoring call_declined during cancellation');
    return;
  }
  
  removeSocketListeners();
  stopTimer();
  isBreakthroughInProgress.value = false; // Reset so user can retry
  // Refresh before any store reset that would clear roomId
  refreshCallLogFromRoomId();
  const now = new Date();
  callEndTime.value = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  callState.value = "not-answered";
};

const onCallEnded = () => {
  // Ignore if we're actively cancelling (prevents flash)
  if (isCancelling.value) {
    console.log('[CallDialog] Ignoring call_ended during cancellation');
    return;
  }
  
  removeSocketListeners();
  stopTimer();
  isBreakthroughInProgress.value = false; // Reset so user can retry
  // Refresh before any store reset that would clear roomId
  refreshCallLogFromRoomId();
  const now = new Date();
  callEndTime.value = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  callState.value = "completed";
};

const registerSocketListeners = () => {
  signalingService.on("call_accepted", onCallAccepted);
  signalingService.on("call_declined", onCallDeclined);
  signalingService.on("call_ended", onCallEnded);
};

onBeforeUnmount(() => {
  removeSocketListeners();
  stopTimer();
});

const formattedDuration = computed(() => {
  const minutes = Math.floor(callDuration.value / 60);
  const seconds = callDuration.value % 60;
  return `${minutes}m ${seconds}s`;
});

const formattedCallDuration = computed(() => {
  const minutes = Math.floor(callDuration.value / 60);
  const seconds = callDuration.value % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
});

const formattedCallDurationFull = computed(() => {
  const hours = Math.floor(callDuration.value / 3600);
  const minutes = Math.floor((callDuration.value % 3600) / 60);
  const seconds = callDuration.value % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
});

watch(
  () => props.modelValue,
  async (newVal) => {
    console.log('[CallDialog] modelValue changed:', newVal, 'showBreakthrough:', props.showBreakthrough);
    if (!newVal) {
      // Dialog closing - clean up everything
      removeSocketListeners(); // Extra safeguard to prevent any late events
      stopTimer();
      callState.value = "ready";
      callDuration.value = 0;
      callAttempts.value = 1;
      isCancelling.value = false; // Reset cancelling flag
    } else if (newVal && props.showBreakthrough) {
      console.log('[CallDialog] Opening in breakthrough mode');
      callState.value = "breakthrough";
      previousState.value = "ready";
    }
    // Normal call opens in "ready" state - nurse must click "Call Now" button
  }
);

const patientInitials = computed(() => {
  if (!props.patientInfo?.patientName) return "SJ";
  const names = props.patientInfo.patientName.split(" ");
  if (names.length >= 2) {
    return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
  }
  return props.patientInfo.patientName.substring(0, 2).toUpperCase();
});

const displayReason = computed(() => {
  if (!props.patientInfo?.reason) return "";
  
  // For conditional discharge, show category with reason in parentheses
  if (props.patientInfo.status === "condition-discharge") {
    return `Conditional Discharge (${props.patientInfo.reason})`;
  }
  
  // For other statuses, show reason as-is
  return props.patientInfo.reason;
});

const statusBadgeColor = computed(() => {
  if (!props.patientInfo) return "#EEF5EF";
  if (props.patientInfo.status === "discharge-ready") {
    return "#EEF5EF";
  } else if (props.patientInfo.status === "condition-discharge") {
    return "#FEF3E8";
  } else if (props.patientInfo.status === "new-admit") {
    return "#E8EEFB";
  }
  return "#EEF5EF";
});

const statusBadgeTextColor = computed(() => {
  if (!props.patientInfo) return "#25883B";
  if (props.patientInfo.status === "discharge-ready") {
    return "#25883B";
  }
  return "#222222";
});

const formatDischargeTime = computed(() => {
  return formatElapsedTime(props.patientInfo?.dischargeOrderTime);
});

const overdueMinutes = computed(() => {
  if (!props.patientInfo?.dischargeOrderTime || !props.patientInfo?.isOverdue) return '';
  return formatOverdueTime(props.patientInfo.dischargeOrderTime);
});

const handleClose = () => {
  removeSocketListeners();
  stopTimer();
  callState.value = "ready";
  callDuration.value = 0;
  feedbackExpanded.value = false;
  feedbackText.value = "";
  emit("update:modelValue", false);
};

const primeCallAudioContext = async () => {
  if (typeof window === "undefined") return;

  try {
    const AudioContextCtor =
      window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) return;

    // Keep a shared context alive so later SDK audio operations don't depend on
    // route-mounted code being inside a click handler.
    const win = window as unknown as { __vcCallAudioContext?: AudioContext };
    if (!win.__vcCallAudioContext) {
      win.__vcCallAudioContext = new AudioContextCtor();
    }

    const ctx = win.__vcCallAudioContext;
    if (ctx.state === "suspended") {
      await ctx.resume();
    }

    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    gain.gain.value = 0.00001;
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.01);
  } catch (err) {
    console.warn("[CallDialog] Audio context prime failed:", err);
  }
};

const handleConfirm = async () => {
  console.log('[CallDialog] handleConfirm called');
  await primeCallAudioContext();
  callState.value = "calling";
  startTimer();
  registerSocketListeners();
  console.log('[CallDialog] Emitting confirm event');
  emit("confirm");
};

const handleCancelCall = async () => {
  console.log('[CallDialog] handleCancelCall called, current callState:', callState.value);
  
  // Set cancelling flag to prevent any WebSocket event handlers from changing state
  isCancelling.value = true;
  
  // CRITICAL: Reset ALL state BEFORE closing to prevent any flash
  removeSocketListeners();
  stopTimer();
  callDuration.value = 0;
  callState.value = "ready"; // Reset state first!
  
  // CRITICAL: Close dialog IMMEDIATELY after state reset
  emit('update:modelValue', false);
  
  const sessionId = callStore.sessionId;
  const roomId = callStore.roomId; // Capture roomId before endCall() clears it
  console.log('[CallDialog] sessionId from store:', sessionId);
  
  if (sessionId) {
    console.log('[CallDialog] Calling endSession for sessionId:', sessionId);
    try {
      await callApiClient.endSession(sessionId);
      console.log('[CallDialog] endSession succeeded');
    } catch (err) {
      console.error('[CallDialog] endSession failed:', err);
    }
    signalingService.notifyCallEnded(sessionId, "nurse-001");
    callStore.endCall();
    
    // Refresh call log AFTER endSession completes to get updated status
    if (roomId) {
      const patient = patientStore.patients.find((p) => p.room.roomId === roomId);
      if (patient) {
        await patientStore.refreshPatientCallLog(patient.patientId).catch(() => {});
      }
    }
  } else {
    console.warn('[CallDialog] No sessionId found in store - call may not have been created yet');
  }
  
  // Reset cancelling flag after cleanup completes
  isCancelling.value = false;
};

const handleCallAgain = async () => {
  await primeCallAudioContext();
  callAttempts.value++;
  callState.value = "calling";
  startTimer();
  registerSocketListeners();
  // Re-run confirmCall to create a new session (old session already ended)
  emit("confirm");
};

const handleBreakthroughCall = () => {
  previousState.value = callState.value === "not-answered" ? "not-answered" : "ready";
  callState.value = "breakthrough";
  selectedBreakthroughReason.value = null;
};

const handleBackFromBreakthrough = () => {
  callState.value = previousState.value;
  selectedBreakthroughReason.value = null;
};

const handleReasonSelect = async (reasonId: string) => {
  if (reasonId === "other") {
    showOtherInput.value = !showOtherInput.value;
    if (showOtherInput.value) {
      selectedBreakthroughReason.value = reasonId;
    } else {
      selectedBreakthroughReason.value = null;
      otherReasonText.value = "";
    }
  } else {
    selectedBreakthroughReason.value = reasonId;
    showOtherInput.value = false;
    
    // Get readable reason text
    const reason = breakthroughReasons.find(r => r.id === reasonId)?.label || reasonId;
    
    await executeBreakthrough(reason);
  }
};

const handleOtherReasonSubmit = async () => {
  if (otherReasonText.value.trim()) {
    await executeBreakthrough(otherReasonText.value.trim());
  }
};

const executeBreakthrough = async (reason: string) => {
  // Prevent duplicate calls
  if (isBreakthroughInProgress.value) {
    console.warn('Breakthrough already in progress, ignoring duplicate call');
    return;
  }
  
  isBreakthroughInProgress.value = true;
  
  try {
    let sessionId = callStore.sessionId;
    
    // If no active session, create one first (skip incoming_call notification)
    if (!sessionId) {
      if (!props.patientInfo?.room?.roomId) {
        console.error('No patient room information available');
        errorMessage.value = 'Unable to initiate breakthrough call. Patient room information missing.';
        showError.value = true;
        isBreakthroughInProgress.value = false;
        return;
      }
      
      // Create session WITHOUT sending incoming_call (breakthrough will send breakthrough_call instead)
      const session = await callApiClient.createSession(props.patientInfo.room.roomId, {
        recordingEnabled: true,
        skipIncomingNotification: true, // Critical: Don't show Accept/Decline on patient device
      });
      
      sessionId = session.sessionId;
      
      // Join the session as nurse (get video credentials)
      const joinResponse = await callApiClient.joinSession(sessionId, 'nurse');
      
      // Set session in call store with credentials
      callStore.setSession(
        sessionId, 
        props.patientInfo.room.roomId, 
        session.provider, 
        joinResponse.session?.credentials || session.credentials
      );
      
      // Join socket.io session room to receive call events
      signalingService.joinSession(sessionId);
    } else {
      // Session already exists (from normal call flow)
      // Nurse has already joined the session, just ensure we're in the socket.io room
      signalingService.joinSession(sessionId);
    }
    
    // Call breakthrough API (notifies patient device via WebSocket with breakthrough_call event)
    await callApiClient.initiateBreakthrough(sessionId, reason);
    
    // Transition to "calling" state (dialog stays open showing "Calling [Patient]...")
    callState.value = "calling";
    startTimer();
    registerSocketListeners(); // Listen for call_accepted, call_ended events
    
    // Dialog will stay open in "calling" state until:
    // - Patient auto-connects and sends call_accepted event → onCallAccepted fires → navigates to call view
    // - Call fails or is cancelled → onCallEnded/onCallDeclined fires
    
  } catch (error) {
    console.error('Breakthrough call failed:', error);
    errorMessage.value = 'Failed to initiate breakthrough call. Please try again.';
    showError.value = true;
    isBreakthroughInProgress.value = false;
    
    // Reset to previous state on error
    callState.value = previousState.value;
    stopTimer();
    
    // Reset form state
    selectedBreakthroughReason.value = null;
    showOtherInput.value = false;
    otherReasonText.value = "";
  }
};

const toggleCallDetails = () => {
  callDetailsExpanded.value = !callDetailsExpanded.value;
};

const handleLeaveFeedback = () => {
  feedbackExpanded.value = true;
};

const handleSubmitFeedback = () => {
  if (feedbackText.value.trim()) {
    console.log("Feedback submitted:", feedbackText.value);
    emit("update:modelValue", false);
  }
};
</script>

<style lang="scss" scoped>
.call-dialog {
  border-radius: 16px !important;
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden !important;
}

.call-dialog__close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 0.8) 40%,
    rgba(255, 255, 255, 0.2) 80%,
    rgba(255, 255, 255, 0) 100%
  );
  backdrop-filter: blur(8px);

  &:hover {
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 0.85) 40%,
      rgba(255, 255, 255, 0.3) 80%,
      rgba(255, 255, 255, 0) 100%
    );
  }
}

.call-dialog__header {
  padding: 32px 48px 24px;
  text-align: center;
  background: #ffffff;
}

.call-dialog__title {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 24px;
  line-height: 1.25em;
  color: #222222;
  margin: 0 0 8px 0;
}

.call-dialog__patient-name {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5em;
  color: #6a5d8a;
  margin-bottom: 16px;
}

.call-dialog__status-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.call-dialog__status-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.75em;
}

.call-dialog__discharge-badge {
  padding: 2px 8px;
  border-radius: 4px;
  background: #f0f0f0;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.75em;
  color: #222222;
}

.call-dialog__overdue-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #d73d17;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.75em;
  color: #ffffff;
}

.call-dialog__content {
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1 1 auto;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 4px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;

    &:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  }
}

.call-dialog__scrollable-content {
  padding: 0 24px 100px;
}

.call-dialog__alert {
  margin-bottom: 24px;

  :deep(.v-alert__prepend) {
    margin-right: 12px;
  }
}

.call-dialog__alert-text {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.5em;
  color: #222222;
}

.call-dialog__section {
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
}

.call-dialog__section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 10px 0;
  margin-bottom: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }
}

.call-dialog__section-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.call-dialog__section-title {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1em;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #6a5d8a;
}

.call-dialog__badge {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 12px;
  color: #222222;
}

.call-dialog__participants {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.call-dialog__participant {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #ffffff;
  border-radius: 14px;
}

.call-dialog__avatar-wrapper {
  position: relative;
  flex-shrink: 0;
}

.call-dialog__avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6b46c1 0%, #9d7ce3 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: #ffffff;

  &--nurse {
    background: linear-gradient(135deg, #795de9 0%, #a78fed 100%);
  }
}

.call-dialog__status-dot {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #25883b;
  border: 2px solid #ffffff;
}

.call-dialog__participant-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.call-dialog__participant-name {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.428em;
  color: #222222;
}

.call-dialog__participant-role {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.5em;
  color: #6a5d8a;
}

.call-dialog__action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.call-dialog__add-btn {
  background-color: #e3ddf3 !important;
  color: #222222 !important;
  text-transform: none;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0;
  padding: 8px 20px !important;
  height: 40px !important;

  :deep(.v-icon) {
    color: #222222;
  }
}

.call-dialog__footer {
  padding: 24px;
  background: linear-gradient(
    to bottom,
    rgba(247, 247, 247, 0) 0%,
    rgba(247, 247, 247, 0.3) 30%,
    rgba(247, 247, 247, 0.7) 100%
  );
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 0 0 16px 16px;
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 5;
  flex-shrink: 0;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(247, 247, 247, 0) 0%,
      rgba(247, 247, 247, 0.3) 30%,
      rgba(247, 247, 247, 0.7) 100%
    );
    pointer-events: none;
    z-index: -1;
  }
}

.call-dialog__call-btn {
  background: #795de9 !important;
  background-color: #795de9 !important;
  color: #ffffff !important;
  text-transform: none;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 2em;
  letter-spacing: 0.0125em;
  padding: 0 32px;
  height: 56px;
  min-width: auto;
  width: auto;

  :deep(.v-icon) {
    color: #ffffff;
    font-size: 1.4rem !important;
  }

  :deep(.mdi:before) {
    font-size: 1.4rem !important;
  }

  :deep(.v-btn__overlay) {
    display: none;
  }
}

.v-btn--size-large .mdi:before {
  font-size: 1.4rem !important;
}

.call-dialog--calling,
.call-dialog--not-answered,
.call-dialog--breakthrough {
  height: 725px !important;
  max-height: 725px !important;
  overflow: hidden !important;

  .call-dialog__content,
  .call-dialog__footer {
    display: none !important;
  }
}

.call-dialog--completed {
  .call-dialog__content,
  .call-dialog__footer {
    display: none !important;
  }
}

.call-dialog__calling-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 42px 25px;
  height: 725px;
}

.call-dialog__calling-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 32px;
  flex: 1;
}

.call-dialog__calling-avatar {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px;
}

.call-dialog__calling-avatar-inner {
  position: relative;
  width: 130px;
  height: 130px;
  border-radius: 50%;
  border: 4px solid #795de9;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e3ddf3;
}

.call-dialog__calling-avatar-pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 2px solid rgba(161, 142, 240, 0.4);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.3);
    opacity: 0;
  }
}

.call-dialog__calling-initials {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 48px;
  color: #795de9;
  z-index: 1;
}

.call-dialog__calling-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.call-dialog__calling-title {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 32px;
  line-height: 1.25em;
  letter-spacing: 0.0025em;
  color: #222222;
  margin: 0;
}

.call-dialog__calling-status {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5em;
  letter-spacing: 0.0015em;
  text-align: center;
  color: #6a5d8a;
  max-width: 580px;
}

.call-dialog__calling-timer {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5em;
  letter-spacing: 0.0015em;
  color: #222222;
  margin-top: 8px;
}

.call-dialog__cancel-btn {
  position: absolute;
  bottom: 42px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #d73d17 !important;
  color: #ffffff !important;
  text-transform: none;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 2em;
  letter-spacing: 0.0125em;
  padding: 16px 24px;
  height: auto;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;

  :deep(.v-icon) {
    color: #ffffff;
  }
}

.call-dialog__not-answered-state {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  padding: 42px 25px;
  height: 725px;
  gap: 58px;
}

.call-dialog__not-answered-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 32px;
  flex: 1;
}

.call-dialog__not-answered-icon {
  display: flex;
  justify-content: center;
  align-items: center;
}

.call-dialog__not-answered-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.call-dialog__not-answered-title {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 32px;
  line-height: 1.25em;
  letter-spacing: 0.0025em;
  color: #222222;
  margin: 0;
}

.call-dialog__not-answered-subtitle {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5em;
  letter-spacing: 0.0015em;
  text-align: center;
  color: #6a5d8a;
}

.call-dialog__not-answered-actions {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
}

.call-dialog__call-again-btn {
  background-color: #795de9 !important;
  color: #ffffff !important;
  text-transform: none;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 2em;
  letter-spacing: 0.0125em;
  padding: 16px 38px;
  height: auto;
  box-shadow: none !important;
  display: flex;
  align-items: center;
  justify-content: center;

  :deep(.v-icon) {
    color: #ffffff;
  }
}

.call-dialog__breakthrough-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.call-dialog__breakthrough-btn {
  background-color: #e3ddf3 !important;
  color: #222222 !important;
  text-transform: none;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 2em;
  letter-spacing: 0.0125em;
  padding: 16px 38px;
  height: auto;
  box-shadow: none !important;
  display: flex;
  align-items: center;
  justify-content: center;

  :deep(.v-icon) {
    color: #222222 !important;
  }
}

.call-dialog__breakthrough-text {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.625em;
  letter-spacing: -0.01074em;
  text-align: center;
  color: #6a5d8a;
  max-width: 350px;
}

.call-dialog__back-btn {
  position: absolute;
  top: 13.75px;
  left: 25px;
  z-index: 10;
  background: #ffffff;
  width: 56px;
  height: 56px;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
}

.call-dialog__breakthrough-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 42px 25px;
  height: 725px;
  gap: 32px;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;

    &:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  }

  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.call-dialog__breakthrough-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  width: 100%;
}

.call-dialog__breakthrough-icon {
  display: flex;
  justify-content: center;
  align-items: center;
}

.call-dialog__breakthrough-warning {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5em;
  text-align: center;
  color: #222222;
}

.call-dialog__breakthrough-reasons {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  width: 100%;
}

.call-dialog__breakthrough-label {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5em;
  color: #222222;
}

.call-dialog__breakthrough-options {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
}

.call-dialog__breakthrough-option {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 9px;
  padding: 12px;
  border: 1px solid #d4d1de;
  border-radius: 12px;
  cursor: pointer;
  background: #ffffff;
  transition: all 0.2s ease;

  &:hover {
    background: #f6f5fb;
  }

  &--selected {
    border-color: #291758 !important;
    background: #f6f5fb;
  }

  &--disabled {
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  }
}

.call-dialog__breakthrough-option-text {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5em;
  color: #222222;
  flex: 1;
}

.call-dialog__other-input-wrapper {
  margin-top: 12px;
  padding: 0 4px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.call-dialog__other-input {
  font-family: "Proxima Nova", sans-serif;

  :deep(.v-field) {
    border-radius: 12px;
    border-color: #d4d1de;
  }

  :deep(.v-field__input) {
    font-family: "Proxima Nova", sans-serif;
    font-size: 16px;
    line-height: 1.5em;
    padding: 12px;
    min-height: auto;
  }

  :deep(.v-field--focused) {
    border-color: #291758;
  }
}

.call-dialog__continue-btn {
  background-color: #795de9 !important;
  color: #ffffff !important;
  text-transform: none;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 2em;
  letter-spacing: 0.0125em;
  padding: 16px 38px;
  height: auto;
  box-shadow: none !important;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.call-dialog__completed-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 39px 24px 0;
  gap: 24px;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 100%;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 8px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(106, 93, 138, 0.3);
    border-radius: 3px;

    &:hover {
      background: rgba(106, 93, 138, 0.5);
    }
  }
}

.call-dialog__completed-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
}

.call-dialog__completed-title {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 32px;
  line-height: 1.25em;
  letter-spacing: 0.0025em;
  text-align: center;
  color: #000000;
  margin: 0;
}

.call-dialog__completed-patient {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5em;
  text-align: center;
  color: #6a5d8a;
}

.call-dialog__completed-status {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 0 34px;
  width: 462px;
}

.call-dialog__completed-sections {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  width: 100%;
}

.call-dialog__completed-participants {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 4px 0 0;
}

.call-dialog__completed-participant {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-radius: 14px;
}

.call-dialog__completed-details {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
}

.call-dialog__detail-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 8px 12px 8px 0;
  border-radius: 14px;
}

.call-dialog__detail-label {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.4285em;
  color: #6a5d8a;
  flex: 1;
  padding: 1px 0;
}

.call-dialog__detail-value {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5em;
  color: #222222;
  display: flex;
  align-items: center;
  gap: 4px;
  height: 28px;
}

.call-dialog__completed-footer {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 24px 12px 16px;
  width: 510px;
  margin-left: -24px;
  margin-right: -24px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.19) 0%, rgba(255, 255, 255, 1) 66%);
}

.call-dialog__footer-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;

  &--feedback {
    width: 100%;
    padding: 0 12px;
    justify-content: center;
  }
}

.call-dialog__close-btn-footer {
  background-color: #ffffff !important;
  color: #222222 !important;
  border: 1.5px solid #d4d1de !important;
  text-transform: none;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 2em;
  letter-spacing: 0.0125em;
  padding: 12px 24px;
  height: auto;
  box-shadow: none !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

.call-dialog__submit-btn {
  background-color: #795de9 !important;
  color: #ffffff !important;
  text-transform: none;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 2em;
  letter-spacing: 0.0125em;
  padding: 12px 24px;
  height: auto;
  box-shadow: none !important;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    background-color: #d4d1de !important;
    color: #999999 !important;
  }
}

.call-dialog__feedback-btn,
.call-dialog__close-btn-footer {
  background-color: #ffffff !important;
  color: #222222 !important;
  border: 1.5px solid #d4d1de !important;
  text-transform: none;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 2em;
  letter-spacing: 0.0125em;
  padding: 12px 24px;
  height: auto;
  box-shadow: none !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

.call-dialog__submit-btn {
  background-color: #795de9 !important;
  color: #ffffff !important;
  text-transform: none;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 2em;
  letter-spacing: 0.0125em;
  padding: 12px 24px;
  height: auto;
  box-shadow: none !important;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    background-color: #d4d1de !important;
    color: #999999 !important;
  }
}

.call-dialog__feedback-section {
  padding-top: 0;
}

.call-dialog__feedback-textarea {
  font-family: "Proxima Nova", sans-serif;

  :deep(.v-field) {
    background: #f6f5fb !important;
    background-color: #f6f5fb !important;
    border: 1px solid #d4d1de;
    border-radius: 12px;
    padding-right: 0 !important;
  }

  :deep(.v-field__input) {
    font-family: "Proxima Nova", sans-serif;
    font-size: 14px;
    line-height: 1.5em;
    padding: 12px 16px;
    min-height: 100px;
  }

  :deep(.v-field__field) {
    padding: 0 !important;
  }

  :deep(.v-field__clearable),
  :deep(.v-field__append-inner) {
    display: none !important;
  }

  :deep(textarea) {
    background: #f6f5fb !important;
    background-color: #f6f5fb !important;
    resize: none !important;
  }

  :deep(::placeholder) {
    color: rgba(34, 34, 34, 0.5);
    font-family: "Proxima Nova", sans-serif;
    font-size: 14px;
  }

  :deep(.v-field--focused) {
    border-color: #291758;
  }
}
</style>
